import React, { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, LineElement, Title, Tooltip, Legend, PointElement, Filler } from "chart.js";
import { DateTime } from "luxon";
import { Badge } from "@radix-ui/themes";
import { useTheme } from "../../../context/ThemeContext";
import "../ChartColours.css";

ChartJS.register(CategoryScale, LinearScale, LineElement, Title, Tooltip, Legend, PointElement, Filler);

const getCSSVariable = (variable) => getComputedStyle(document.documentElement).getPropertyValue(variable).trim();

export const DwellByTimeOfDayChart = ({ data, startTimeMilliseconds, endTimeMilliseconds }) => {
  const { appearance } = useTheme();
  const [fontColor, setFontColor] = useState("");
  const [gridColour, setGridColour] = useState("");
  const [chartPointsBgColour, setChartPointsBgColour] = useState("");
  const [chartContainerRef, setChartContainerRef] = useState(null);
  const [badgePosition, setBadgePosition] = useState({ left: 0, top: 0, display: "none" });
  const [chartDwellColour, setChartDwellColour] = useState("");
  const [chartDwellFillColour, setChartDwellFillColour] = useState("");

  useEffect(() => {
    const fontColourLight = getCSSVariable("--chart-font-colour-light");
    const fontColourDark = getCSSVariable("--chart-font-colour-dark");
    const gridColourLight = getCSSVariable("--chart-grid-colour-light");
    const gridColourDark = getCSSVariable("--chart-grid-colour-dark");
    const chartPointsBgColourLight = getCSSVariable("--chart-points-bg-colour-light");
    const chartPointsBgColourDark = getCSSVariable("--chart-points-bg-colour-dark");
    const dwellColour = getCSSVariable("--chart-guest-border-colour");
    const dwellFillColour = getCSSVariable("--chart-guest-fill-colour");
    const dwellFillColourLight = getCSSVariable("--chart-guest-fill-colour-light");
    const dwellColourLight = getCSSVariable("--chart-guest-border-colour-light");

    if (appearance === "dark") {
      setFontColor(fontColourDark);
      setGridColour(gridColourDark);
      setChartPointsBgColour(chartPointsBgColourDark);
      setChartDwellFillColour(dwellFillColour);
      setChartDwellColour(dwellColour);
    } else {
      setFontColor(fontColourLight);
      setGridColour(gridColourLight);
      setChartPointsBgColour(chartPointsBgColourLight);
      setChartDwellFillColour(dwellFillColourLight);
      setChartDwellColour(dwellColourLight);
    }
  }, [appearance]);

  // Process the data for the chart
  let processed_chart_data = Array.from({ length: 24 }, (_, hour) => ({
    hour,
    dwell: 0,
  }));

  if (data?.data?.chart_data?.hourly) {
    // Fill in the data from the API
    data.data.chart_data.hourly.forEach((entry) => {
      const hour = parseInt(entry.time_period);
      if (hour >= 0 && hour < 24) {
        processed_chart_data[hour] = {
          hour,
          dwell: entry.avg_dwell,
        };
      }
    });
  }

  const formatXAxisLabel = (hour) => {
    return DateTime.fromObject({ hour }).toFormat("HH:mm");
  };

  // Get current hour for badge
  const currentHour = DateTime.now().hour;

  const isCurrentDayInRange = () => {
    const endDate = DateTime.fromMillis(endTimeMilliseconds);
    const now = DateTime.now();
    return now.hasSame(endDate, "day");
  };

  const chartData = {
    labels: processed_chart_data.map((entry, index) => (index === currentHour && isCurrentDayInRange() ? "" : formatXAxisLabel(entry.hour))),
    datasets: [
      {
        label: "Avg. guest dwell",
        data: processed_chart_data.map((entry) => entry.dwell),
        backgroundColor: chartDwellFillColour,
        borderColor: chartDwellColour,
        fill: true,
        borderWidth: 1,
        tension: 0,
        pointBackgroundColor: chartPointsBgColour,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 1000,
      onComplete: function (animation) {
        if (chartContainerRef) {
          const chart = animation.chart;
          const xAxis = chart.scales.x;
          const currentHourX = xAxis.getPixelForValue(currentHour);
          const currentHourY = xAxis.top + xAxis.options.ticks.padding;

          setBadgePosition({
            left: currentHourX,
            top: currentHourY - 6,
            display: isCurrentDayInRange() ? "block" : "none",
          });
        }
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        mode: "index",
        intersect: false,
        usePointStyle: true,
        boxWidth: 6,
        callbacks: {
          title: function (tooltipItems) {
            return formatXAxisLabel(processed_chart_data[tooltipItems[0].dataIndex].hour);
          },
          label: function (context) {
            return `${context.dataset.label}: ${context.parsed.y.toFixed(1)} mins`;
          },
          labelColor: function (context) {
            return {
              borderColor: context.dataset.borderColor,
              backgroundColor: context.dataset.borderColor,
            };
          },
          labelPointStyle: function (context) {
            return {
              pointStyle: "circle",
              rotation: 0,
            };
          },
        },
        bodyFont: {
          family: "Figtree",
        },
        titleFont: {
          family: "Figtree",
        },
      },
    },
    scales: {
      x: {
        ticks: {
          color: fontColor,
          font: {
            family: "Figtree",
            size: 12,
          },
          padding: 8,
          callback: function (value, index) {
            return index % 2 === 0 ? this.getLabelForValue(value) : "";
          },
          maxRotation: 0,
        },
        grid: {
          display: true,
          color: gridColour,
          borderWidth: 1,
          drawTicks: false,
        },
        border: {
          dash: [2, 2],
        },
      },
      y: {
        beginAtZero: true,
        position: "right",
        ticks: {
          color: fontColor,
          font: {
            family: "Figtree",
            size: 12,
          },
          padding: 8,
          stepSize: 1, // Force integer steps
          callback: function (value) {
            if (Number.isInteger(value)) {
              return `${value} mins`;
            }
            return null; // Hide non-integer ticks
          },
        },
        grid: {
          display: true,
          color: gridColour,
          borderWidth: 1,
          drawTicks: false,
        },
        border: {
          dash: [2, 2],
        },
      },
    },
  };

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }} ref={setChartContainerRef}>
      <Line data={chartData} options={options} style={{ width: "100%", height: "100%" }} />
      <div
        style={{
          position: "absolute",
          left: `${badgePosition.left}px`,
          top: `${badgePosition.top}px`,
          transform: "translate(-50%, 0)",
          display: badgePosition.display,
          pointerEvents: "none",
          zIndex: 10,
        }}
      >
        <Badge variant="soft" size="1">
          {formatXAxisLabel(currentHour)}
        </Badge>
      </div>
    </div>
  );
};
